// 컬럼 시스템 통합 관리 파일
// 사용처: farmfree-functions, farmfree-admin
// 컬럼 정의, 라벨, 기본 설정을 한 곳에서 관리

// ===== 컬럼 키 정의 =====
export const COLUMN_KEYS = {
  checkbox: 'checkbox',
  id: 'id',
  dateCreated: 'dateCreated',
  receiverName: 'receiverName',
  receiverPhone1: 'receiverPhone1',
  receiverPhone2: 'receiverPhone2',
  receiverAddress: 'receiverAddress',
  receiverDetailAddress: 'receiverDetailAddress',
  receiverZipCode: 'receiverZipCode',
  senderName: 'senderName',
  senderPhone1: 'senderPhone1',
  senderPhone2: 'senderPhone2',
  senderAddress: 'senderAddress',
  senderDetailAddress: 'senderDetailAddress',
  senderZipCode: 'senderZipCode',
  product: 'product',
  quantity: 'quantity',
  processStatus: 'processStatus',
  sourceOrder: 'sourceOrder',
  sourcePhone: 'sourcePhone',
  sourceCompany: 'sourceCompany',

  // 택배 관련
  courierProvider: 'courierProvider', // deliveryItem.courierProvider
  courierAccountId: 'courierAccountId', // deliveryItem.courierAccountId
  courierTrackingId: 'courierTrackingId', // deliveryItem.courierTrackingId
  trackingNumber: 'trackingNumber', // deliveryItem.trackingNumber
  deliveryStatus: 'deliveryStatus', // deliveryItem.status

  // 결제 관련
  totalMatched: 'totalMatched', // paymentInfo.totalMatched
  paymentStatus: 'paymentStatus', // paymentInfo.status
} as const

// 타입 추출
export type ColumnKey = (typeof COLUMN_KEYS)[keyof typeof COLUMN_KEYS]

// ===== 컬럼 라벨 매핑 =====
export const COLUMN_LABELS: Record<ColumnKey, string> = {
  checkbox: '',
  id: '주문ID',
  dateCreated: '생성일',
  receiverName: '수령인',
  receiverPhone1: '수령인연락처',
  receiverPhone2: '수령인연락처2',
  receiverAddress: '배송주소',
  receiverDetailAddress: '상세주소',
  receiverZipCode: '우편번호',
  senderName: '발송인',
  senderPhone1: '발송인연락처',
  senderPhone2: '발송인연락처2',
  senderAddress: '발송인주소',
  senderDetailAddress: '발송인상세주소',
  senderZipCode: '발송인우편번호',
  product: '상품명',
  quantity: '수량',
  processStatus: '처리상태',
  sourceOrder: '주문출처',
  sourcePhone: '출처연락처',
  sourceCompany: '출처회사',
  courierProvider: '택배회사',
  courierAccountId: '택배계정ID',
  courierTrackingId: '택배추적ID',
  trackingNumber: '송장번호',
  deliveryStatus: '배송상태',
  totalMatched: '매칭금액',
  paymentStatus: '결제상태',
}

// ===== 컬럼 설정 타입 정의 =====
export interface ColumnConfig {
  id: string // 팩토리 패턴을 위한 고유 식별자 (key와 동일)
  key: string // 컬럼 키 (id와 동일하지만 기존 호환성 유지)
  label: string
  visible: boolean
  order: number
}

export interface ModeColumnSettings {
  columns: ColumnConfig[]
}

export interface ColumnSettings {
  id: string // 팩토리 패턴용 고정 ID (예: 'column_settings')
  view: ModeColumnSettings
  payment: ModeColumnSettings
  tracking: ModeColumnSettings
  lastUpdated: Date
}

// ===== 모든 컬럼 마스터 리스트 =====
const ALL_COLUMN_KEYS = Object.keys(COLUMN_KEYS) as ColumnKey[]

export const ALL_COLUMNS: ColumnConfig[] = ALL_COLUMN_KEYS.map((key, index) => ({
  id: key, // id와 key는 동일
  key,
  label: COLUMN_LABELS[key],
  visible: false, // 기본값 false, 각 모드에서 재정의
  order: index,
}))

// ===== 모드별 기본 컬럼 설정 =====

// 보기 모드 - visible 키 목록 (타입 검증)
// ⚠️ 중요: 이 배열의 순서가 실제 표시 순서가 됩니다
const VIEW_VISIBLE_KEYS = [
  'checkbox',
  'dateCreated',
  'receiverName',
  'receiverPhone1',
  'receiverPhone2',
  'receiverAddress',
  'receiverDetailAddress',
  'product',
  'quantity',
  'courierProvider',
  'trackingNumber',
  'deliveryStatus',
  'senderName',
  'senderPhone1',
  'senderAddress',
  'senderDetailAddress',
  'sourceOrder',
  'sourcePhone',
] as const satisfies readonly ColumnKey[]

// 보기 모드 기본 컬럼 (배열 순서 = 표시 순서)
export const DEFAULT_VIEW_COLUMNS: ColumnConfig[] = (() => {
  // 1. 보이는 컬럼들 (배열 순서대로 order 부여)
  const visibleColumns: ColumnConfig[] = VIEW_VISIBLE_KEYS.map((key, index) => ({
    id: key,
    key,
    label: COLUMN_LABELS[key],
    visible: true,
    order: index,
  }))

  // 2. 안 보이는 컬럼들 (뒤쪽에 추가)
  const invisibleColumns: ColumnConfig[] = ALL_COLUMNS.filter(
    (col) => !(VIEW_VISIBLE_KEYS as readonly string[]).includes(col.key)
  ).map((col, index) => ({
    ...col,
    visible: false,
    order: VIEW_VISIBLE_KEYS.length + index,
  }))

  return [...visibleColumns, ...invisibleColumns]
})()

// 입금확인 모드 - visible 키 목록 (타입 검증)
// ⚠️ 중요: 이 배열의 순서가 실제 표시 순서가 됩니다
const PAYMENT_VISIBLE_KEYS = [
  'checkbox',
  'dateCreated',
  'receiverName',
  'receiverPhone1',
  'product',
  'quantity',
  'totalMatched',
  'paymentStatus',
  'courierProvider',
  'courierAccountId',
  'courierTrackingId',
  'trackingNumber',
  'deliveryStatus',
] as const satisfies readonly ColumnKey[]

// 입금확인 모드 기본 컬럼 (배열 순서 = 표시 순서)
export const DEFAULT_PAYMENT_COLUMNS: ColumnConfig[] = (() => {
  // 1. 보이는 컬럼들 (배열 순서대로 order 부여)
  const visibleColumns: ColumnConfig[] = PAYMENT_VISIBLE_KEYS.map((key, index) => ({
    id: key,
    key,
    label: COLUMN_LABELS[key],
    visible: true,
    order: index,
  }))

  // 2. 안 보이는 컬럼들 (뒤쪽에 추가)
  const invisibleColumns: ColumnConfig[] = ALL_COLUMNS.filter(
    (col) => !(PAYMENT_VISIBLE_KEYS as readonly string[]).includes(col.key)
  ).map((col, index) => ({
    ...col,
    visible: false,
    order: PAYMENT_VISIBLE_KEYS.length + index,
  }))

  return [...visibleColumns, ...invisibleColumns]
})()

// 송장번호 모드 - visible 키 목록 (타입 검증)
// ⚠️ 중요: 이 배열의 순서가 실제 표시 순서가 됩니다
const TRACKING_VISIBLE_KEYS = [
  'checkbox',
  'dateCreated',
  'receiverName',
  'receiverPhone1',
  'receiverAddress',
  'product',
  'quantity',
  'courierProvider',
  'courierAccountId',
  'courierTrackingId',
  'trackingNumber',
  'deliveryStatus',
  'sourceOrder',
] as const satisfies readonly ColumnKey[]

// 송장번호 모드 기본 컬럼 (배열 순서 = 표시 순서)
export const DEFAULT_TRACKING_COLUMNS: ColumnConfig[] = (() => {
  // 1. 보이는 컬럼들 (배열 순서대로 order 부여)
  const visibleColumns: ColumnConfig[] = TRACKING_VISIBLE_KEYS.map((key, index) => ({
    id: key,
    key,
    label: COLUMN_LABELS[key],
    visible: true,
    order: index,
  }))

  // 2. 안 보이는 컬럼들 (뒤쪽에 추가)
  const invisibleColumns: ColumnConfig[] = ALL_COLUMNS.filter(
    (col) => !(TRACKING_VISIBLE_KEYS as readonly string[]).includes(col.key)
  ).map((col, index) => ({
    ...col,
    visible: false,
    order: TRACKING_VISIBLE_KEYS.length + index,
  }))

  return [...visibleColumns, ...invisibleColumns]
})()

// ===== 기본 설정 객체 =====
export const DEFAULT_COLUMN_SETTINGS: ColumnSettings = {
  id: 'column_settings', // 고정 ID
  view: { columns: DEFAULT_VIEW_COLUMNS },
  payment: { columns: DEFAULT_PAYMENT_COLUMNS },
  tracking: { columns: DEFAULT_TRACKING_COLUMNS },
  lastUpdated: new Date(),
}

// ===== 헬퍼 함수들 =====

// 카테고리별 기본 컬럼 가져오기 (deep copy로 반환)
export function getDefaultColumnsForMode(mode: 'view' | 'payment' | 'tracking'): ColumnConfig[] {
  let source: ColumnConfig[]

  switch (mode) {
    case 'view':
      source = DEFAULT_VIEW_COLUMNS
      break
    case 'payment':
      source = DEFAULT_PAYMENT_COLUMNS
      break
    case 'tracking':
      source = DEFAULT_TRACKING_COLUMNS
      break
    default:
      source = DEFAULT_VIEW_COLUMNS
  }

  // Deep copy: 각 객체를 새로 생성하여 원본 보호
  return source.map((col) => ({ ...col }))
}

// 토글 가능한 컬럼 목록 (checkbox 제외)
export function getTogglableColumns(columns: ColumnConfig[]): ColumnConfig[] {
  return columns.filter((col) => col.key !== 'checkbox')
}

// 표시할 컬럼 목록 (순서대로 정렬)
// ⚠️ 중요: checkbox는 order 값과 관계없이 항상 맨 앞에 배치
export function getVisibleColumns(columns: ColumnConfig[]): ColumnConfig[] {
  const visibleColumns = columns.filter((col) => col.visible)

  // checkbox와 나머지 컬럼 분리
  const checkboxColumn = visibleColumns.find((col) => col.key === 'checkbox')
  const otherColumns = visibleColumns.filter((col) => col.key !== 'checkbox')

  // 나머지 컬럼들은 order 순서대로 정렬
  const sortedOtherColumns = otherColumns.sort((a, b) => a.order - b.order)

  // checkbox가 있으면 맨 앞에, 없으면 나머지만 반환
  return checkboxColumn ? [checkboxColumn, ...sortedOtherColumns] : sortedOtherColumns
}

// 타입 가드 함수
export function isColumnKey(key: string): key is ColumnKey {
  return Object.values(COLUMN_KEYS).includes(key as ColumnKey)
}

// ===== 추가 타입들 =====
export type ViewMode = 'view' | 'payment'
export type TrackingViewMode = ViewMode | 'tracking'

export interface ColumnSettingsUpdateRequest {
  mode: ViewMode | 'tracking'
  columns: ColumnConfig[]
}

export interface ColumnSettingsResponse {
  isSuccess: boolean
  message?: string
  data?: ColumnSettings
}

export interface ColumnDragEvent {
  oldIndex: number
  newIndex: number
  item: ColumnConfig
}
